// src/pages/MySells.jsx
import React, { useState, useEffect } from 'react';
import { useSellRequest } from '../context/SellRequestContext';
import { inspectionService } from '../services/InspectionService';
import { Link } from 'react-router-dom';
import { ChevronRight, AlertCircle, Clock } from 'lucide-react';
import InspectionCard from '../components/Inspection/InspectionCard';
import RescheduleModal from '../components/Inspection/RescheduleModal';
import InspectionModal from '../components/Inspection/InspectionModal';
import '../styles/MySells.css';


const MySells = () => {
  const { sellRequests, fetchSellRequests, loading: sellLoading, error: sellError } = useSellRequest();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchSellRequests(),
          fetchInspections()
        ]);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchInspections = async () => {
    try {
      const response = await inspectionService.getInspections();
      const inspectionList = response.data || response;
      setInspections(Array.isArray(inspectionList) ? inspectionList : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching inspections:', err);
      setError('Failed to load inspections');
      setInspections([]);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      submitted: '#FFD60A',
      inspection_pending: '#FF6B6B',
      inspection_completed: '#4ECDC4',
      offer_generated: '#45B7D1',
      offer_accepted: '#2ECC71',
      offer_rejected: '#E74C3C',
      documents_pending: '#F39C12',
      payment_completed: '#27AE60',
      completed: '#16A085'
    };
    return statusColors[status] || '#999';
  };

  const getStatusLabel = (status) => {
    const labels = {
      submitted: 'Submitted',
      inspection_pending: 'Pending Inspection',
      inspection_completed: 'Inspected',
      offer_generated: 'Quote Generated',
      offer_accepted: 'Quote Accepted',
      offer_rejected: 'Quote Rejected',
      documents_pending: 'Documents Pending',
      documents_submitted: 'Documents Submitted',
      payment_pending: 'Payment Pending',
      payment_completed: 'Payment Done',
      pickup_scheduled: 'Pickup Scheduled',
      car_collected: 'Car Collected',
      rc_transfer_in_progress: 'RC Transfer',
      completed: 'Completed'
    };
    return labels[status] || status;
  };

  const handleViewDetails = (inspection) => {
    setSelectedInspection(inspection);
    setIsDetailModalOpen(true);
  };

  const handleReschedule = (inspection) => {
    setSelectedInspection(inspection);
    setIsRescheduleModalOpen(true);
  };

  const handleRescheduleSubmit = async (formData) => {
    try {
      setRescheduleLoading(true);
      await inspectionService.updateInspection(selectedInspection.id, formData);
      alert('Inspection rescheduled successfully!');
      setIsRescheduleModalOpen(false);
      await fetchInspections();
    } catch (err) {
      console.error('Reschedule error:', err);
      throw new Error(err.message || 'Failed to reschedule inspection');
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleCancel = async (inspectionId) => {
    if (!window.confirm('Are you sure you want to cancel this inspection? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(inspectionId);
      await inspectionService.cancelInspection(inspectionId);
      alert('Inspection cancelled successfully!');
      await fetchInspections();
    } catch (err) {
      console.error('Cancel error:', err);
      alert('Failed to cancel inspection. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  // ✅ UPDATED: Filter inspections based on active tab
  const filteredInspections = activeTab === 'all'
    ? inspections
    : inspections.filter(insp => {
      // ✅ PENDING TAB: Only show 'pending' status
      if (activeTab === 'pending') {
        return insp.status === 'pending';
      }
      // ✅ COMPLETED TAB: Show 'completed' AND 'confirmed' (both are payment done)
      if (activeTab === 'completed') {
        return ['completed', 'confirmed'].includes(insp.status);  // ← UPDATED: Include 'confirmed'
      }
      // ✅ CANCELLED TAB: Only show 'cancelled' status
      if (activeTab === 'cancelled') {
        return insp.status === 'cancelled';
      }
      return true;
    });

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>My Car Inspections</h1>
          <p style={styles.subtitle}>Track all your car inspection bookings</p>
        </div>
        <div style={styles.loadingState}>
          <div className="spinner"></div>
          <p>Loading your inspections...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>My Car Inspections</h1>
        <p style={styles.subtitle}>Track all your car inspection bookings</p>
      </div>

      {/* Tabs */}
      <div style={styles.tabsContainer}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'all' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('all')}
        >
          All ({inspections.length})
        </button>
        
        {/* ✅ PENDING TAB - Shows count of pending inspections */}
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'pending' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('pending')}
        >
          Pending ({inspections.filter(i => i.status === 'pending').length})
        </button>
        
        {/* ✅ COMPLETED TAB - Shows count of completed inspections (including confirmed) */}
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'completed' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('completed')}
        >
          Completed ({inspections.filter(i => ['completed', 'confirmed'].includes(i.status)).length})
        </button>
        
        {/* ✅ CANCELLED TAB - Shows count of cancelled inspections */}
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'cancelled' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('cancelled')}
        >
          Cancelled ({inspections.filter(i => i.status === 'cancelled').length})
        </button>
      </div>

      {/* Content */}
      <div style={styles.contentContainer}>
        {error && (
          <div style={styles.alert}>
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {filteredInspections.length === 0 ? (
          <div style={styles.emptyState}>
            <Clock size={48} color="#ccc" />
            <h3>No inspections found</h3>
            <p>
              {activeTab === 'all' && "You haven't booked any car inspections yet"}
              {activeTab === 'pending' && "You don't have any pending inspections"}
              {activeTab === 'completed' && "You don't have any completed inspections"}
              {activeTab === 'cancelled' && "You don't have any cancelled inspections"}
            </p>
            {activeTab === 'all' && (
              <Link to="/sell-car" style={styles.startButton}>
                Start Selling Your Car
              </Link>
            )}
          </div>
        ) : (
          <div style={styles.listContainer}>
            {filteredInspections.map(inspection => (
              <InspectionCard
                key={inspection.id}
                inspection={inspection}
                onViewDetails={handleViewDetails}
                onReschedule={handleReschedule}
                onCancel={handleCancel}
              />
            ))}
          </div>
        )}
      </div>

      {/* CTA Section - Start Selling Another Car */}
      {inspections.length > 0 && (
        <div style={styles.ctaSection}>
          <div style={styles.ctaContent}>
            <h2 style={styles.ctaTitle}>Want to Sell Another Car?</h2>
            <p style={styles.ctaSubtitle}>
              Get instant quotes and free inspection for your next vehicle
            </p>
            <Link to="/sell-car" style={styles.ctaButton}>
              Start Selling Your Car Now
              <ChevronRight size={20} style={{ marginLeft: '8px' }} />
            </Link>
          </div>
        </div>
      )}

      {/* Modals */}
      <InspectionModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        inspection={selectedInspection}
      />

      <RescheduleModal
        isOpen={isRescheduleModalOpen}
        onClose={() => {
          setIsRescheduleModalOpen(false);
          setSelectedInspection(null);
        }}
        inspection={selectedInspection}
        onSubmit={handleRescheduleSubmit}
        loading={rescheduleLoading}
      />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#FAFAFA',
    paddingBottom: '60px'
  },
  header: {
    background: 'linear-gradient(135deg, #003566 0%, #001d3d 100%)',
    padding: '50px 40px',
    textAlign: 'center',
    color: '#FAFAFA'
  },
  title: {
    fontSize: '2.5rem',
    marginBottom: '10px',
    fontWeight: '700'
  },
  subtitle: {
    fontSize: '1.1rem',
    color: '#FFD60A'
  },
  tabsContainer: {
    maxWidth: '900px',
    margin: '40px auto 0',
    padding: '0 20px',
    display: 'flex',
    gap: '10px',
    borderBottom: '2px solid #e0e0e0',
    overflow: 'auto'
  },
  tab: {
    backgroundColor: 'transparent',
    border: 'none',
    padding: '15px 20px',
    fontSize: '1rem',
    color: '#666',
    cursor: 'pointer',
    fontWeight: '500',
    borderBottom: '3px solid transparent',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap'
  },
  tabActive: {
    color: '#003566',
    borderBottomColor: '#FFD60A'
  },
  contentContainer: {
    maxWidth: '900px',
    margin: '40px auto',
    padding: '0 20px'
  },
  alert: {
    padding: '15px 20px',
    backgroundColor: '#fee',
    border: '2px solid #f44336',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: '#d32f2f',
    marginBottom: '20px'
  },
  loadingState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#666'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: '#fff',
    borderRadius: '15px'
  },
  listContainer: {
    display: 'grid',
    gap: '20px'
  },
  startButton: {
    display: 'inline-block',
    marginTop: '20px',
    backgroundColor: '#003566',
    color: '#FAFAFA',
    padding: '12px 30px',
    borderRadius: '10px',
    textDecoration: 'none',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  ctaSection: {
    maxWidth: '900px',
    width: '100%',
    margin: '60px auto',  
    padding: '50px 40px',
    backgroundColor: 'linear-gradient(135deg, #003566 0%, #001d3d 100%)',
    backgroundImage: 'linear-gradient(135deg, #003566 0%, #001d3d 100%)',
    borderRadius: '20px',
    textAlign: 'center',
    color: '#fff',
    boxShadow: '0 10px 40px rgba(0, 53, 102, 0.2)',
    position: 'relative',
    overflow: 'hidden'
  },
  ctaContent: {
    position: 'relative',
    zIndex: 2
  },
  ctaTitle: {
    fontSize: '2rem',
    fontWeight: '700',
    marginBottom: '15px',
    marginTop: 0
  },
  ctaSubtitle: {
    fontSize: '1.1rem',
    color: '#FFD60A',
    marginBottom: '30px',
    marginTop: 0
  },
  ctaButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: '#FFD60A',
    color: '#003566',
    padding: '15px 40px',
    borderRadius: '50px',
    textDecoration: 'none',
    fontWeight: '700',
    fontSize: '1.1rem',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 8px 20px rgba(255, 214, 10, 0.3)',
    transform: 'translateY(0)'
  }
};

// Add hover effect via CSS for the CTA button
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  a[style*="background: linear-gradient(135deg, #003566"]]:hover {
    transform: translateY(-3px) !important;
    box-shadow: 0 12px 30px rgba(255, 214, 10, 0.4) !important;
  }
  
  a[style*="#FFD60A"]:hover {
    transform: translateY(-3px) !important;
    box-shadow: 0 12px 30px rgba(255, 214, 10, 0.4) !important;
  }
`;
typeof document !== 'undefined' && document.head.appendChild(styleSheet);

export default MySells;